'use client';
import React, { useState } from 'react';
import { Download, Eye, FileText, Calendar, HardDrive, Tag } from 'lucide-react';
import type { Document } from '@/lib/types';
import { formatDate, formatFileSize } from '@/lib/types';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import PreviewModal from './PreviewModal';

interface DocumentCardProps {
  document: Document;
}

export default function DocumentCard({ document: doc }: DocumentCardProps) {
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const categoryName = (doc.category as any)?.name ?? '';

  const getSignedUrl = async (action: 'preview' | 'download'): Promise<string | null> => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase.functions.invoke('get-public-document', {
        body: { slug: doc.slug, action },
      });
      if (error || !data?.signedUrl) {
        // Fallback: if no storage_path, show friendly error
        return null;
      }
      return data.signedUrl as string;
    } catch {
      return null;
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // If no storage path, show info message
      if (!doc.storage_path) {
        toast.info('Sample document', {
          description: 'This is a sample entry. Upload a real PDF via the admin dashboard.',
        });
        return;
      }
      const url = await getSignedUrl('download');
      if (!url) {
        toast.error('Download failed', {
          description: 'The document could not be downloaded. Please try again or contact the college office.',
        });
        return;
      }
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || `${doc.slug}.pdf`;
      link.click();
      toast.success(`Downloading ${doc.title}`, {
        description: doc.original_filename,
      });
    } catch {
      toast.error('Download failed', {
        description: 'The document could not be downloaded. Please try again or contact the college office.',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (!doc.storage_path) {
      toast.info('Sample document', {
        description: 'This is a sample entry. Upload a real PDF via the admin dashboard.',
      });
      return;
    }
    const url = await getSignedUrl('preview');
    if (url) {
      setPreviewUrl(url);
    } else {
      toast.error('Preview unavailable', {
        description: 'Could not load the document preview.',
      });
      return;
    }
    setShowPreview(true);
  };

  return (
    <>
      <article className="card flex flex-col h-full transition-all duration-200 hover:shadow-card-hover hover:-translate-y-0.5 group">
        {/* Card header */}
        <div className="p-4 sm:p-5 flex-1 flex flex-col">
          {/* PDF icon + category badge */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors duration-150">
              <FileText size={20} className="text-primary" aria-hidden="true" />
            </div>
            {categoryName && (
              <span className="badge bg-blue-50 text-primary border border-blue-100 text-xs max-w-[140px] truncate">
                <Tag size={10} aria-hidden="true" />
                {categoryName}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-sm sm:text-base font-bold text-foreground leading-snug mb-1.5 line-clamp-2">
            {doc.title}
          </h3>

          {/* Description */}
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
            {doc.description}
          </p>

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar size={11} aria-hidden="true" />
              {formatDate(doc.updated_at)}
            </span>
            {doc.file_size > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <HardDrive size={11} aria-hidden="true" />
                {formatFileSize(doc.file_size)}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex gap-2">
          <button
            onClick={handlePreview}
            aria-label={`Preview ${doc.title}`}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border border-border rounded-lg text-muted-foreground hover:text-primary hover:border-primary transition-all duration-150 active:scale-95"
          >
            <Eye size={13} aria-hidden="true" />
            <span>Preview</span>
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            aria-label={`Download ${doc.title} PDF`}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm font-bold bg-accent text-accent-foreground rounded-lg hover:bg-green-700 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <Download size={13} aria-hidden="true" />
                <span>Download PDF</span>
              </>
            )}
          </button>
        </div>
      </article>

      {showPreview && (
        <PreviewModal
          doc={{ ...doc, file_url: previewUrl || '' } as any}
          onClose={() => { setShowPreview(false); setPreviewUrl(null); }}
        />
      )}
    </>
  );
}